/**
 * La caja «Ante el SAT» (corte 2 de los certificados del SAT; diseño docs/producto/diseno-ficha-certificados-sat.md, §2.0): lo que dicen
 * los certificados del SAT y la consulta de la CURP, con la persona al centro. Todo sale de `ante_el_sat` del documento argus.hallazgos/1
 * (nexo.hallazgos, 20261016230000): esta función no redacta ni decide, sólo ordena lo que la base ya escribió en las tres partes de cada
 * tarjeta (quién es, qué empresas representa, con quién más actúa) y pone los rótulos de certificados-textos.ts.
 *
 * Vive en el servicio porque la pinta también el anexo del PDF del Buscador (buscador-pdf-html.ts), y el servicio no alcanza app/shared
 * al correr. Las dos pantallas la usan por app/argus-team-web/web/src/lib/ante-el-sat.ts, que le pasa su forma de escribir una fecha.
 * Sin React ni red. Lee el bloque con cuidado: una clave que falta es una lista vacía, nunca un error.
 */
import { DATO_SAT, SAT } from './certificados-textos.ts';

/** Lo que se lee del bloque (`$defs/anteElSat` y `$defs/tarjetaSat` del contrato), sin depender de app/shared. */
export interface TarjetaSatDoc {
  nivel?: 'confirmada' | 'posible'; causa?: string; nombre?: string; nombre_de?: 'lista_ofac' | 'consulta_curp'; sin_nombre?: string;
  en_lista?: { ref: string; nivel: 'confirmada' | 'posible'; causa?: string }[];
  otras_listas?: { autoridad: string; texto: string }[];
  datos?: { campo: string; valor: string }[];
  identificadores?: { tipo: 'curp' | 'rfc'; valor: string }[];
  correos?: string[];
  poblacion?: { codigo: string; consulta?: string; texto: string };
  /** 20261017225000: los certificados que traen la CURP de esta persona con un error. */
  curp_con_error?: { curp: string; texto: string }[];
  texto?: string; tambien?: string;
  representa?: { ref?: string; nombre: string; en_lista: boolean; nivel?: 'confirmada' | 'posible'; texto: string }[];
  mismo_dia?: { fecha: string; texto: string }[];
  correos_compartidos?: { correo: string; texto: string }[];
  con_ella?: { texto: string }[];
}
export interface AnteElSatDoc {
  personas?: TarjetaSatDoc[]; representantes?: TarjetaSatDoc[]; empresa_posible?: string;
  correos_compartidos?: { correo: string; texto: string }[]; ayuda: string; omitido?: { hallazgos: number; personas: number }; omitido_texto?: string;
}

/** Un dato de la persona: su rótulo (S01) y su valor, tal cual lo dice la fuente (una fecha, como la escribe quien pinta). */
export interface DatoSat { rotulo: string; valor: string }
/** Una empresa de la lista de OFAC con que coincide la persona, con su nivel («posible» se marca). */
export interface EnListaSat { ref: string; posible: boolean; causa: string | null }
/** Una línea de la tarjeta, con la marca «posible» si la liga sólo es posible. */
export interface LineaSat { texto: string; posible: boolean; ref: string | null }

export interface TarjetaVista {
  /** Clave estable (el orden de la base). */
  clave: string;
  /** El nombre; sin nombre, `sinNombre` lo dice (S02). */
  nombre: string | null;
  /** «según el Registro Nacional de Población» (S02) cuando el nombre sale de la consulta de la CURP. */
  nombreDe: string | null;
  sinNombre: string | null;
  /** La tarjeta entera es sólo posible (la persona puede no ser el representante): su causa. */
  posible: boolean;
  causa: string | null;
  /** En la lista de OFAC (puede ser más de una entidad; cada una con su nivel). */
  enLista: EnListaSat[];
  otrasListas: string[];
  /** 1. Quién es. */
  datos: DatoSat[];
  /** Lo que dice la consulta de la CURP y, desde 20261017225000, el certificado que la trae con un error (una línea). */
  poblacion: string | null;
  /** 2. Qué empresas representa: en la ficha de una persona, una línea por empresa; en la de una empresa, la línea vista desde ella. */
  representa: LineaSat[];
  /** 3. Con quién más actúa: las otras empresas, «el mismo día», el correo compartido y quién más la representa. */
  conQuien: string[];
}

export interface VistaAnteElSat {
  titulo: string;
  /** La ficha es de una empresa (las líneas son las de esa empresa: sin el subtítulo de la persona). */
  empresa: boolean;
  /** La empresa de la ficha sólo puede ser la de los certificados (S14): va al principio, con la marca «posible». */
  empresaPosible: string | null;
  tarjetas: TarjetaVista[];
  /** En la ficha de una empresa, los correos de sus certificados que aparecen en los de otras empresas (S17). */
  correos: string[];
  /** «Hay [n] más. Ver todos los certificados.» (S19), si no cupieron todos. */
  omitido: string | null;
  /** S31. */
  ayuda: string;
  /** Hay alguna marca «posible» en la caja (para su ayuda). */
  hayPosible: boolean;
}

const lista = <T>(x: T[] | undefined | null): T[] => (Array.isArray(x) ? x : []);
const rotulo = (campo: string): string | null => (DATO_SAT as Record<string, string>)[campo] ?? null;

function tarjeta(t: TarjetaSatDoc, i: number, fecha: (v: string) => string): TarjetaVista {
  const datos: DatoSat[] = [];
  // Quién es: primero los identificadores (CURP y RFC, para confirmar que es tu cliente), luego lo de la consulta de la CURP y el correo.
  for (const x of lista(t.identificadores)) datos.push({ rotulo: DATO_SAT[x.tipo], valor: x.valor });
  for (const d of lista(t.datos)) {
    // El nombre ya es el título de la tarjeta; si el del Registro Nacional de Población es otro (el título es el de la lista de OFAC), se
    // muestra: es justo lo que sirve para comparar con tu cliente (revisión del corte 2a, hallazgo 6).
    if (d.campo === 'nombre' && t.nombre && d.valor.trim().toUpperCase() === t.nombre.trim().toUpperCase()) continue;
    const r = rotulo(d.campo);
    if (r) datos.push({ rotulo: r, valor: d.campo === 'nacimiento' && /^\d{4}(-\d{2}){0,2}$/.test(d.valor) ? fecha(d.valor) : d.valor });
  }
  // D1 (Bernardo, 2026-10-16): con la liga posible la base no manda el correo; aquí sólo se pinta lo que viene.
  if (lista(t.correos).length > 0) datos.push({ rotulo: DATO_SAT.correo, valor: lista(t.correos).join(', ') });
  const representa: LineaSat[] = t.representa
    ? lista(t.representa).map(r => ({ texto: r.texto, posible: r.nivel === 'posible', ref: r.ref ?? null }))
    : t.texto ? [{ texto: t.texto, posible: false, ref: null }] : [];
  const conQuien = [
    ...(t.tambien ? [t.tambien] : []),
    ...lista(t.mismo_dia).map(m => m.texto),
    ...lista(t.correos_compartidos).map(c => c.texto),
    ...lista(t.con_ella).map(c => c.texto),
  ];
  return {
    clave: `t${i}`,
    nombre: t.nombre ?? null,
    nombreDe: t.nombre && t.nombre_de === 'consulta_curp' ? SAT.segunPoblacion : null,
    sinNombre: t.nombre ? null : t.sin_nombre ?? null,
    posible: t.nivel === 'posible',
    causa: t.nivel === 'posible' ? t.causa ?? null : null,
    enLista: lista(t.en_lista).map(e => ({ ref: e.ref, posible: e.nivel === 'posible', causa: e.causa ?? null })),
    otrasListas: lista(t.otras_listas).map(o => o.texto),
    datos,
    poblacion: [t.poblacion?.texto, ...lista(t.curp_con_error).map(e => e.texto)].filter((x): x is string => !!x).join(' ') || null,
    representa,
    conQuien,
  };
}

/** La caja de un bloque, o null si el sujeto no tiene certificados (la ficha de siempre). `fecha`: cómo escribe una fecha quien pinta. */
export function vistaDeAnteElSat(a: AnteElSatDoc | null | undefined, tipoSujeto: string, fecha: (v: string) => string): VistaAnteElSat | null {
  if (!a || typeof a !== 'object') return null;
  const empresa = tipoSujeto === 'empresa';
  // La ficha de una persona lee `personas`; la de una empresa, `representantes`. La otra lista nunca se mezcla (revisión, hallazgo 7).
  const tarjetas = (empresa ? a.representantes : a.personas) ?? [];
  const vista = lista(tarjetas).map((t, i) => tarjeta(t, i, fecha));
  return {
    titulo: empresa ? SAT.tituloEmpresa : SAT.tituloPersona,
    empresa,
    empresaPosible: a.empresa_posible ?? null,
    tarjetas: vista,
    correos: lista(a.correos_compartidos).map(c => c.texto),
    omitido: a.omitido_texto ?? null,
    ayuda: a.ayuda,
    hayPosible: !!a.empresa_posible || vista.some(t => t.posible || t.enLista.some(e => e.posible) || t.representa.some(r => r.posible)),
  };
}

/** Los textos de una caja (para las pruebas: todo texto visible está en el documento o en un catálogo). */
export function textosDeVista(v: VistaAnteElSat | null): string[] {
  if (!v) return [];
  return [v.titulo, v.empresaPosible, v.omitido, v.ayuda, ...v.correos, ...v.tarjetas.flatMap(t => [
    t.nombre, t.nombreDe, t.sinNombre, t.causa, t.poblacion, ...t.otrasListas, ...t.datos.flatMap(d => [d.rotulo, d.valor]),
    ...t.representa.map(r => r.texto), ...t.conQuien,
  ])].filter((x): x is string => typeof x === 'string' && x.length > 0);
}
